import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "../../../providers/ThemeContext";
import { CategoryCardStyles } from "./CategoryCardStyles";

interface Props {
  title: string;
  subtitle?: string;
  desc?: string;
  points?: string[];
  img: string;
  alt: string;
  to?: string;
  reverse?: boolean;
  ctaLabel?: string;
  index?: number;
}

const CategoryCard = ({
  title,
  subtitle,
  desc,
  points,
  img,
  alt,
  to,
  reverse = false,
  ctaLabel = "Learn More",
  index = 0,
}: Props) => {
  const { theme } = useTheme();
  const s = CategoryCardStyles(theme);
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [hovered, setHovered] = useState(false);

  const cardStyle: React.CSSProperties = {
    ...s.card,
    ...(reverse ? s.cardReverse : s.cardDefault),
    ...(isMobile ? s.cardSm : {}),
    ...(index === 0 ? { borderTop: "none" } : {}),
  };

  const textColStyle: React.CSSProperties = {
    ...s.textCol,
    ...(isMobile ? s.textColSm : {}),
    // Mirror padding for reversed cards on desktop
    ...(reverse && !isMobile
      ? { paddingInlineStart: 48, paddingInlineEnd: 0 }
      : {}),
  };

  // Format index as 01, 02 ...
  const indexLabel = String(index + 1).padStart(2, "0");

  const body = (
    <article
      style={cardStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* ── Text column ── */}
      <div style={textColStyle}>
        {/* Faded background number */}
        <span style={s.indexNumber} aria-hidden="true">
          {indexLabel}
        </span>

        <div style={s.textInner}>
          {subtitle && <p style={s.subtitle}>{subtitle}</p>}

          <h3 style={s.title}>{title}</h3>

          {Array.isArray(points) && points.length > 0 ? (
            <ul style={s.pointsList}>
              {points.map((p, i) => (
                <li key={i}>{p}</li>
              ))}
            </ul>
          ) : desc ? (
            <p style={s.desc}>{desc}</p>
          ) : null}

          {to && (
            <div style={s.ctaWrap}>
              <span
                style={{
                  ...s.cta,
                  opacity: hovered ? 0.88 : 1,
                  transform: hovered ? "translateY(-1px)" : "none",
                }}
              >
                {ctaLabel}
                <span
                  style={{
                    ...s.ctaArrow,
                    transform: hovered ? "translateX(3px)" : "translateX(0)",
                  }}
                  aria-hidden="true"
                >
                  →
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Media column ── */}
      <div style={s.mediaCol}>
        <img
          src={img}
          alt={alt}
          style={{
            ...s.image,
            transform: hovered ? "scale(1.04)" : "scale(1)",
          }}
          loading="lazy"
        />
      </div>
    </article>
  );

  return to ? (
    <Link to={to} style={s.linkReset} aria-label={title}>
      {body}
    </Link>
  ) : (
    body
  );
};

export default CategoryCard;
